/**
 * AQ-Cloud2 application shell.
 *
 * Three-row × two-column layout, pinned to the viewport (`h-screen
 * overflow-hidden`) so the header, sidebar, and status bar never scroll out
 * of view — only `<main>` scrolls internally:
 *
 *   ┌────────────── header (56px) ───────────────────────┐
 *   │ sidebar │  main (overflow-y-auto)                  │
 *   ├─────────┴──────────────────────────────────────────┤
 *   │ status bar (32px, optional)                        │
 *   └────────────────────────────────────────────────────┘
 *
 * Children render inside `<main>`. Pages that own contextual status content
 * use the `useStatusBar()` hook (StatusBarProvider hoists this above).
 */
import React from 'react'
import { cn } from '@/lib/utils'

interface AppShellProps {
  header?: React.ReactNode
  sidebar?: React.ReactNode
  /** Optional sticky bottom status bar (typically <StatusBar />). */
  statusBar?: React.ReactNode
  /**
   * Optional right-docked panel slot. Used by the Project Viewer for the
   * line-item detail panel. Sized by its own component (typically 320–480px).
   *
   * Responsive contract (`.claude/rules/responsive.md`): the slot itself is
   * breakpoint-agnostic — it renders whatever `rightPanel` is below `lg` too.
   * A fixed right column doesn't work on a phone, so the *panel component*
   * (see `RightContextPanel`) is responsible for swapping itself to a
   * `Sheet` below `lg` and only occupying flex-row width on `lg+`. Keep this
   * slot dumb; don't add width/flex constraints here that would fight the
   * panel's own responsive sizing.
   */
  rightPanel?: React.ReactNode
  children: React.ReactNode
  className?: string
}

export const AppShell = ({
  header,
  sidebar,
  statusBar,
  rightPanel,
  children,
  className,
}: AppShellProps): JSX.Element => {
  return (
    <div className={cn('h-screen overflow-hidden bg-background flex flex-col', className)}>
      {header}
      <div className="flex flex-1 min-h-0">
        {sidebar}
        <main
          className="flex-1 min-w-0 flex flex-col bg-background relative overflow-y-auto"
          id="main-content"
        >
          {children}
        </main>
        {rightPanel}
      </div>
      {statusBar}
    </div>
  )
}
